/**
 * What each administration noun points at, and what changes it.
 *
 * A declaration rather than five hand-built panels, because the relationships
 * repeat: four collections name a legal Company and a paying Company, two name
 * a managing Company, two belong to an Operation. Written out per noun, that is
 * the same editor four times over — and the fourth is the one that keeps the
 * old behaviour after somebody fixes the first three.
 *
 * Each row says four things: what to call it, what kind of editor draws it,
 * where to read the current value from a record, and what to call to change it.
 * `RelationshipPanel` needs nothing else.
 *
 * **A Company is not here.** Everything points at Companies; a Company points
 * at nothing, which is why `companies` holds an empty list rather than being
 * left out — a noun with no relationships is a fact worth stating.
 */

/** A Company named by identifier, with the denormalized name beside it. */
function companyRow({ key, label, read, apply, help }) {
    return { key, label, help, kind: 'company', client: 'companies', read, apply }
}

// `legalCompany` and `managementCompany` are `{ companyId, name }`; the payer
// sits one level deeper, under `billingInfo.companyInfo`.
const readLegal = (record) => record.legalCompany ?? null
const readManagement = (record) => record.managementCompany ?? null
const readBilling = (record) => record.billingInfo?.companyInfo ?? null

const LEGAL = {
    key: 'legalCompany',
    label: 'Legal company',
    help: 'The Company this is, legally. Clearing it falls the resolution through to the Operation.',
    read: readLegal,
    apply: (store, id, companyId) => store.setLegalCompany(id, companyId)
}

const MANAGEMENT = {
    key: 'managementCompany',
    label: 'Management company',
    help: 'An outside Company running this Operation. Manager and owner are two Companies on one document.',
    read: readManagement,
    apply: (store, id, companyId) => store.setManagementCompany(id, companyId)
}

// The payer needs a subscription alongside it, which is why this kind is its
// own rather than another `company` row: the server takes
// `{ companyId, stripeSubscriptionId }` together, or `{ companyId: null }`
// alone to clear, and nothing in between.
const BILLING = {
    key: 'billingInfo',
    label: 'Billing company',
    help: 'Who pays, and under which Stripe subscription. A Company carrying no billing facts cannot be a payer.',
    kind: 'billing',
    client: 'companies',
    read: readBilling,
    readSubscription: (record) => record.billingInfo?.stripeSubscriptionId ?? '',
    readStanding: (record) => record.billingInfo?.standing ?? null,
    apply: (store, id, companyId, subscriptionId) => store.setBillingCompany(id, companyId, subscriptionId)
}

/** The Operation a house or a dealership belongs to. Not nullable. */
function operationRow(client) {
    return {
        key: 'operationInfo',
        label: 'Operation',
        help: 'Not clearable: with no Operation there is nothing to resolve a legal identity or a payer through.',
        kind: 'operation',
        client,
        read: (record) => {
            if (record.operationInfo === undefined) {
                return null
            }
            return { companyId: record.operationInfo.operationId, name: record.operationInfo.name }
        },
        apply: (store, id, operationId) => store.setOperation(id, operationId)
    }
}

/**
 * Policies: a keyed map, each entry set and cleared by its own name.
 *
 * The shape of one is the policy's own — the server stores what it is given and
 * says the application validates it against the key. No application defines any
 * yet, so the editor takes JSON rather than pretending to know the fields.
 */
const POLICIES = {
    key: 'policies',
    label: 'Policies',
    help: 'Named settings this document carries. The shape of each is its own.',
    kind: 'policies',
    read: (record) => record.policies ?? {},
    set: (store, id, name, policy) => store.setPolicy(id, name, policy),
    clear: (store, id, name) => store.clearPolicy(id, name)
}

/**
 * Humans barred from this Operation or house.
 *
 * Typed rather than picked, and that is a gap rather than a design: Humans live
 * in the identity Effort and this platform has no collection to search. So an
 * entry can be read and removed, and adding one means knowing an identifier
 * nobody here can look up.
 */
const KNOCKED_OUT_HUMANS = {
    key: 'knockedOutHumans',
    label: 'Knocked-out humans',
    help: 'Replaced whole. Operation knockout always wins — a house may bar beyond it, never inside it.',
    kind: 'replaceList',
    read: (record) => record.knockedOutHumans ?? [],
    columns: [
        { key: 'humanId', label: 'Human ID', placeholder: '24 hex characters' },
        { key: 'name', label: 'Name', placeholder: 'Shown in the list' }
    ],
    blank: () => ({ humanId: '', name: '' }),
    apply: (store, id, list) => store.setKnockedOutHumans(id, list)
}

/** The claims that feed a Dealership's good standing. Replaced whole. */
const CLAIMS = {
    key: 'claims',
    label: 'Claims',
    help: 'Replaced whole: adding one sends the others back with it.',
    kind: 'replaceList',
    read: (record) => record.claims ?? [],
    columns: [
        { key: 'claimType', label: 'Claim type', placeholder: 'dealerLicense' },
        { key: 'claimProperties', label: 'Properties', placeholder: '{ "number": "1" }', json: true }
    ],
    blank: () => ({ claimType: '', claimProperties: {} }),
    apply: (store, id, list) => store.setClaims(id, list)
}

/**
 * Dealerships barred from every house this Operation manages.
 *
 * A member of a set addressed entirely in the path, so each change is its own
 * request and there is no list to send back.
 */
const KNOCKED_OUT_DEALERSHIPS = {
    key: 'knockedOutDealerships',
    label: 'Knocked-out dealerships',
    help: 'Operation-wide. Barred from every house this Operation manages, including ones attached later.',
    kind: 'memberSet',
    client: 'dealerships',
    idKey: 'dealershipId',
    addLabel: 'Knock one out',
    read: (record) => record.knockedOutDealerships ?? [],
    add: (store, id, dealershipId) => store.knockOutDealership(id, dealershipId),
    remove: (store, id, dealershipId) => store.restoreDealership(id, dealershipId)
}

/**
 * The Dealerships allowed to buy at this house, and which of them it has barred.
 *
 * Registration and knockout are two different things on one entry: taking the
 * registration away is not the same as barring, and the bar this lifts is only
 * the one this house set.
 */
const REGISTERED_DEALERSHIPS = {
    key: 'registeredDealerships',
    label: 'Registered dealerships',
    help: 'Who may buy here. House knockout is separate from unregistering, and never lifts the Operation\'s.',
    kind: 'memberSet',
    client: 'dealerships',
    idKey: 'dealershipId',
    addLabel: 'Register one',
    read: (record) => record.registeredDealerships ?? [],
    add: (store, id, dealershipId) => store.registerDealership(id, dealershipId),
    remove: (store, id, dealershipId) => store.unregisterDealership(id, dealershipId),
    toggle: {
        label: 'Barred here',
        reads: (entry) => entry.isKnockedOut === true,
        on: (store, id, dealershipId) => store.knockOutRegisteredDealership(id, dealershipId),
        off: (store, id, dealershipId) => store.restoreRegisteredDealership(id, dealershipId)
    }
}

/**
 * What an Operation manages, asked of the children rather than read off it.
 *
 * An Operation records nothing about what it manages — the contract says so, and
 * the schemas carry no roster. The edge is the child's: a house holds a required
 * `operationInfo.operationId`, indexed for exactly this question. So this row is
 * a query rather than a field, which is why its accessors take the store and
 * every other row's take the record.
 *
 * It is read-only for the same reason. Moving a house is a write on the house,
 * through the Operation picker that already draws on its own screen.
 */
function managedRow({ key, label, help, routeName, readPage, readCount, load }) {
    return { key, label, help, kind: 'managed', routeName, readPage, readCount, load }
}

const MANAGED_AUCTION_HOUSES = managedRow({
    key: 'auctionHouses',
    label: 'Auction houses',
    help: 'The houses this Operation manages. Each one names the Operation; the Operation names none of them.',
    routeName: 'auction-houses',
    readPage: (store) => store.auctionHouses,
    readCount: (store) => store.auctionHouseCount,
    load: (store, id, pagination) => store.loadAuctionHouses(id, pagination)
})

const MANAGED_DEALERSHIPS = managedRow({
    key: 'dealerships',
    label: 'Dealerships',
    help: 'The dealerships this Operation manages. Each one names the Operation; the Operation names none of them.',
    routeName: 'dealerships',
    readPage: (store) => store.dealerships,
    readCount: (store) => store.dealershipCount,
    load: (store, id, pagination) => store.loadDealerships(id, pagination)
})

export const RELATIONSHIPS = {
    'companies': [],

    'auction-operations': [
        MANAGED_AUCTION_HOUSES,
        companyRow(LEGAL),
        companyRow(MANAGEMENT),
        BILLING,
        KNOCKED_OUT_DEALERSHIPS,
        KNOCKED_OUT_HUMANS,
        POLICIES
    ],

    'dealership-operations': [
        MANAGED_DEALERSHIPS,
        companyRow(LEGAL),
        companyRow(MANAGEMENT),
        BILLING,
        POLICIES
    ],

    'auction-houses': [
        operationRow('auctionOperations'),
        companyRow(LEGAL),
        BILLING,
        REGISTERED_DEALERSHIPS,
        KNOCKED_OUT_HUMANS,
        POLICIES
    ],

    'dealerships': [
        operationRow('dealershipOperations'),
        companyRow(LEGAL),
        BILLING,
        CLAIMS,
        POLICIES
    ]
}

/**
 * What a house or dealership resolves through when it holds none of its own.
 *
 * Worth showing rather than leaving blank: an empty "Legal company" on a house
 * does not mean nobody is responsible, it means the Operation is — and that is
 * the difference between a gap and a design.
 */
export function inheritedFrom(record) {
    if (record.operationInfo === undefined) {
        return null
    }
    return {
        name: record.operationInfo.name,
        legalCompany: record.operationInfo.legalCompany ?? null,
        billingCompany: record.operationInfo.billingInfo?.companyInfo ?? null
    }
}
